import { useCampaignCode } from '@campaign-services/hooks/useCampaignCode'
import { Platform } from '@cart-services/hooks/fp-b2b/store-map'
import { useFpB2bCartService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-cart-service'
import { type CountryCode, type ProjectCode } from '@constants'
import { useFastpassMerchantGoogleAnalyticEvent } from '@dni-analysis'
import { useApiCampaignActivityInfo } from '@hooks-api/fortune/useApiCampaignActivityInfo'
import { HStack } from '@pets-ui/HStack/HStack'
import { Link } from '@pets-ui/Link/Link'
import { Text } from '@pets-ui/Text/Text'
import * as stylex from '@stylexjs/stylex'
import { useState } from 'react'
import { useHistory } from 'react-router-dom-v5'
import { resolveBrandIcon } from '../utils/resolve-brand-icon'
import { AppBar, type AppBarProps } from './AppBar/AppBar'
import { AppBarSearch } from './AppBar/AppBarSearch'
import { useAppBar } from './AppBar/useAppBar'
import { AppBarAccount } from './AppBarAccount/AppBarAccount'
import { AppBarCart } from './AppBarCart/AppBarCart'
import { Searcher } from './LegacySearcher/Searcher/Searcher'

const styles = stylex.create({
  root: {
    padding: 12,
  },
})

export const LeftHeader = ({ projectCode }: { projectCode: ProjectCode }) => {
  const { campaignCodeFromUrl } = useCampaignCode()
  const { destructuredData: campaign } = useApiCampaignActivityInfo(campaignCodeFromUrl)
  const brandInfo = campaign?.brand_codes_info[0]
  const history = useHistory()
  const handleToHome = () => {
    history.push(`/merchant/${campaignCodeFromUrl}/product-catalog`)
  }

  const brandIcon = resolveBrandIcon(campaign, projectCode) ?? brandInfo?.brand_image
  return (
    <HStack gap={2} alignItems="center" flexWrap="nowrap">
      <Link onClick={handleToHome}>
        <img src={brandIcon} alt={brandInfo?.brand_name} width={80} style={{ display: 'block' }} />
      </Link>
      <Text variant="body" lineClamp={2}>{campaign?.title}</Text>
    </HStack>
  )
}

const RightHeader = ({ projectCode, countryCode, campaignId }: { projectCode: ProjectCode, countryCode: CountryCode, campaignId: number }) => {
  const [isOpen, setIsOpen] = useState(false)
  const { isCartDisabled, toCartPage, onLogout } = useAppBar()
  const { items } = useFpB2bCartService(campaignId, Platform.MERCHANT)
  const cartItemQuantity = items.filter(item => !item.butler.isDeleted).reduce((acc, item) => acc + item.quantity, 0)
  const {
    sendClickSearchBarEvent,
    sendSearchKeywordEvent,
    sendSearchResultClickEvent,
    sendPastResultClickEvent,
    sendClickMyAccountIconEvent,
    sendBeginCartEvent,
  } = useFastpassMerchantGoogleAnalyticEvent({ projectCode, countryCode, campaignId })

  const handleSearchButtonClick = () => {
    setIsOpen(true)
    sendClickSearchBarEvent()
  }

  const handleCartClick = () => {
    sendBeginCartEvent()
    toCartPage?.()
  }

  return (
    <>
      <HStack alignItems="center" flexWrap="nowrap">
        <AppBarAccount onMyAccountClick={sendClickMyAccountIconEvent} onLogoutClick={onLogout} />
        <AppBarCart isCartDisabled={isCartDisabled} onClick={handleCartClick} cartItemQuantity={cartItemQuantity} />
        <AppBarSearch onClick={handleSearchButtonClick} />
      </HStack>
      <Searcher campaignId={campaignId} isDrawerOpen={isOpen} onDrawerToggle={() => setIsOpen(!isOpen)} projectCode={projectCode} countryCode={countryCode} onSearch={sendSearchKeywordEvent} onResultClick={sendSearchResultClickEvent} onPastResultClick={sendPastResultClickEvent} environment="merchant" />
    </>
  )
}

export const TheMerchantHeader = (props: AppBarProps & { projectCode: ProjectCode, countryCode: CountryCode, campaignId: number }) => {
  const { rootStyle, projectCode, countryCode, campaignId, ...rest } = props
  return (
    <AppBar
      left={<LeftHeader projectCode={projectCode} />}
      right={<RightHeader projectCode={projectCode} countryCode={countryCode} campaignId={campaignId} />}
      rootStyle={[styles.root, rootStyle]}
      {...rest}
    />
  )
}
